/**
 * AI 提示词指令 Hooks
 */
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query';
import {
  aiPromptCommandsApi,
  type AIPromptCommandListParams,
  type AIPromptCommandMenuParams,
} from '@/services/api/ai-prompt-commands';
import type {
  AIPromptCommandCreate,
  AIPromptCommandOrderItem,
  AIPromptCommandTagBatchUpdate,
  AIPromptCommandUpdate,
} from '@/types/ai-prompt-command';

/** Query Key 工厂 */
const promptCommandKeys = {
  all: ['ai-prompt-commands'] as const,
  lists: () => [...promptCommandKeys.all, 'list'] as const,
  list: (params?: AIPromptCommandListParams) => [...promptCommandKeys.lists(), params] as const,
  menus: () => [...promptCommandKeys.all, 'menu'] as const,
  menu: (params?: AIPromptCommandMenuParams) => [...promptCommandKeys.menus(), params] as const,
  details: () => [...promptCommandKeys.all, 'detail'] as const,
  detail: (promptId: string) => [...promptCommandKeys.details(), promptId] as const,
  tags: (orgId?: string | null, outputType?: number) =>
    [...promptCommandKeys.all, 'tags', orgId ?? 'personal', outputType ?? 'all'] as const,
};

/**
 * 查询指令列表（分页）
 */
export function useAIPromptCommandList(params?: AIPromptCommandListParams) {
  return useQuery({
    queryKey: promptCommandKeys.list(params),
    queryFn: () => aiPromptCommandsApi.list(params),
    staleTime: 1000 * 60 * 2,
  });
}

/**
 * 查询指令菜单数据（按输出类型分组）
 * @param outputType 输出类型排序提示
 * @param activeUsageScope 当前业务适用场景排序提示
 */
export function useAIPromptCommandMenu(params?: AIPromptCommandMenuParams) {
  return useQuery({
    queryKey: promptCommandKeys.menu(params),
    queryFn: () => aiPromptCommandsApi.menu(params),
    staleTime: 5 * 60 * 1000,
  });
}

export function invalidateAIPromptCommands(queryClient: QueryClient): void {
  void queryClient.invalidateQueries({ queryKey: promptCommandKeys.all });
}

export function useAIPromptCommandTags(orgId?: string | null, outputType?: number) {
  return useQuery({
    queryKey: promptCommandKeys.tags(orgId, outputType),
    queryFn: () => aiPromptCommandsApi.listTags({ orgId, outputType }),
    placeholderData: keepPreviousData,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * 查询单条指令详情
 */
export function useAIPromptCommandDetail(promptId: string | null, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: promptId
      ? promptCommandKeys.detail(promptId)
      : ['ai-prompt-commands', 'detail', 'disabled'],
    queryFn: () => {
      if (!promptId) throw new Error('缺少指令 ID');
      return aiPromptCommandsApi.getById(promptId);
    },
    enabled: (options?.enabled ?? true) && !!promptId,
    staleTime: 0,
    refetchOnMount: 'always',
  });
}

/**
 * 创建指令
 */
export function useCreateAIPromptCommand() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: AIPromptCommandCreate) => aiPromptCommandsApi.create(data),
    onSuccess: () => {
      invalidateAIPromptCommands(queryClient);
    },
  });
}

export function useCreateAIPromptCommandTag() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: {
      name: string;
      color?: string | null;
      orderIndex?: number;
      orgId?: string | null;
    }) => aiPromptCommandsApi.createTag(data),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: promptCommandKeys.tags(variables.orgId) });
    },
  });
}

export function useUpdateAIPromptCommandTag() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      tagId,
      data,
      orgId,
    }: {
      tagId: string;
      data: { name?: string; color?: string | null; orderIndex?: number };
      orgId?: string | null;
    }) => aiPromptCommandsApi.updateTag(tagId, data, { orgId }),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: promptCommandKeys.tags(variables.orgId) });
      void queryClient.invalidateQueries({ queryKey: promptCommandKeys.lists() });
      void queryClient.invalidateQueries({ queryKey: promptCommandKeys.menus() });
    },
  });
}

export function useDeleteAIPromptCommandTag() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ tagId, orgId }: { tagId: string; orgId?: string | null }) =>
      aiPromptCommandsApi.deleteTag(tagId, { orgId }),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: promptCommandKeys.tags(variables.orgId) });
      void queryClient.invalidateQueries({ queryKey: promptCommandKeys.lists() });
      void queryClient.invalidateQueries({ queryKey: promptCommandKeys.menus() });
    },
  });
}

export function useAssignAIPromptCommandTags() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: AIPromptCommandTagBatchUpdate) => aiPromptCommandsApi.assignTags(data),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: promptCommandKeys.all });
      void queryClient.invalidateQueries({ queryKey: promptCommandKeys.tags(variables.orgId) });
    },
  });
}

export function useRemoveAIPromptCommandTags() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: AIPromptCommandTagBatchUpdate) => aiPromptCommandsApi.removeTags(data),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: promptCommandKeys.all });
      void queryClient.invalidateQueries({ queryKey: promptCommandKeys.tags(variables.orgId) });
    },
  });
}

/**
 * 更新指令
 */
export function useUpdateAIPromptCommand() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      promptId,
      data,
      orgId,
    }: {
      promptId: string;
      data: AIPromptCommandUpdate;
      orgId?: string | null;
    }) => aiPromptCommandsApi.update(promptId, data, { orgId }),
    onSuccess: () => {
      invalidateAIPromptCommands(queryClient);
    },
  });
}

/**
 * 批量更新指令排序
 */
export function useReorderAIPromptCommands() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ items, orgId }: { items: AIPromptCommandOrderItem[]; orgId?: string | null }) =>
      aiPromptCommandsApi.reorder(items, { orgId }),
    onSuccess: () => {
      invalidateAIPromptCommands(queryClient);
    },
  });
}

/**
 * 设置指令置顶状态
 */
export function useSetAIPromptCommandPinned() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      promptId,
      isPinned,
      orgId,
    }: {
      promptId: string;
      isPinned: boolean;
      orgId?: string | null;
    }) => aiPromptCommandsApi.setPinned(promptId, isPinned, { orgId }),
    onSuccess: () => {
      invalidateAIPromptCommands(queryClient);
    },
  });
}

/**
 * 删除指令
 */
export function useDeleteAIPromptCommand() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ promptId, orgId }: { promptId: string; orgId?: string | null }) =>
      aiPromptCommandsApi.delete(promptId, { orgId }),
    onSuccess: () => {
      invalidateAIPromptCommands(queryClient);
    },
  });
}
